import { Addon, ChoicePayload } from './addToCart';
import { buildPersonalisationChoices } from './buildPersonalisationChoices';
import { supportsWebP } from '../utilities/webpDetection';
import { convertCartPayloadToFormData } from '../utilities/atcViaFormData';
import { logError } from '../utilities/logging';
import { validateCartItem } from './atcValidation';
import { imagePropertiesProcessing } from '../utilities/imagePropertiesProcessing';
import { fetchVendor } from './queries/fetchVendor';
import { addBaseGid, removeGid } from '../utilities/handleGid';
import { base64ToFile } from '../utilities/base64ToFile';
import { Selections } from '../context/personalisation-typedefs';
import { Slot } from './personalisationData';

const imageType = supportsWebP() ? 'image/webp' : 'image/png';

interface CreatePayloadOptions {
  choices: ChoicePayload[];
  addons: Addon[];
  visualiserImageUrls: { url: string; groupId: string }[];
  baseProduct: { id: string; quantity: number };
  country: string;
  baseAttributes: Record<string, string>;
  selections: Selections;
  baseProductConfigurationSlotId: string;
  slots: Slot[];
  templateId?: string | null;
}

export const createPayload = async ({
  choices,
  addons,
  visualiserImageUrls,
  baseProduct,
  country,
  baseAttributes,
  selections,
  baseProductConfigurationSlotId,
  slots,
  templateId
}: CreatePayloadOptions): Promise<{
  formData: FormData;
  stringifiedItems: { id: number; quantity: number; properties: Record<string, string> }[];
  featuredImageMapping: Record<
    string,
    { groupId: string; lineItemPropertyKey: string; supportingImageLineItemPropertyKey: string; slotType: string }
  >;
  checkId: string;
  featuredImages: Record<string, File>;
}> => {
  // Used to find the item later if we experience a bad connection.
  const checkId = self.crypto.randomUUID();
  // Create visBlob for IMAGE_UPLOAD slots if visualiserImageUrl is available.
  const visData =
    visualiserImageUrls?.map((groupData, index) => {
      const visIsBase64 = groupData.url?.startsWith('data:');
      // If the URL is a base64 string, convert it to a File object.
      // Otherwise, use the URL as is.
      const convertedItem = visIsBase64
        ? base64ToFile({
            base64String: groupData.url?.split('base64,').at(1),
            mimeType: imageType,
            fileName: `visualiser-image-${index}.${imageType.split('/').at(1)}`
          })
        : groupData.url;
      return {
        url: convertedItem,
        groupId: groupData.groupId,
        file: groupData.file,
        lineItemPropertyKey: groupData.lineItemPropertyKey,
        label: groupData.label,
        // Pass through positioning data for image cropping in line item properties
        positioning: groupData.positioning || null
      };
    }) || [];

  const personalisationChoices = buildPersonalisationChoices({
    choices,
    addons,
    baseProductConfigurationSlotId
  });
  const baseProductId = removeGid({ id: `${baseProduct.id}` });
  const baseVariantId =
    addBaseGid({ dataType: 'ProductVariant' }) +
    '/' +
    String(baseProduct.id).replace(addBaseGid({ dataType: 'ProductVariant' }) + '/', '');
  // Get the vendor for the base product to add to the line item properties.
  const vendor = await fetchVendor({ variantId: baseVariantId, country });

  if (!vendor) {
    logError({
      label: 'addToCart',
      error: new Error('Vendor not found'),
      message: 'Vendor not found',
      extraData: { baseProductId },
      throwError: true
    });
  }

  // Convert baseAttributes to an array from an object to a single key value pairs
  const baseAttributesArray = Object.entries(baseAttributes);
  // Flat map over baseAttributes and find any keys with commas in and split them into an two items with the same value.
  const rawProcessedBaseAttributes = baseAttributesArray.reduce(
    (acc, [key, value]) => {
      if (value !== null) {
        // If the key includes a comma, split it into an array of keys and values.
        if (key.includes(',')) {
          const keys = key.split(',');
          keys.forEach((splitKey) => {
            acc[`${splitKey}`] = value;
          });
        } else {
          acc[`${key}`] = value;
        }
      }

      return acc;
    },
    {} as Record<string, string>
  );

  // When multiple VAS slots each contribute the same key (e.g. two slots with different
  // _template_id values), setWithSuffix in processLineItems writes _template_id and
  // _template_id_2. Normalise so the first also gets _1 when a _2 sibling exists,
  // giving a consistent _template_id_1 / _template_id_2 / _template_id_3 pattern.
  const VAS_CONFLICT_KEYS = ['_vas_type', '_template_id'] as const;
  const processedBaseAttributes = { ...rawProcessedBaseAttributes };
  for (const key of VAS_CONFLICT_KEYS) {
    if (key in processedBaseAttributes && `${key}_2` in processedBaseAttributes) {
      // eslint-disable-next-line security/detect-object-injection
      processedBaseAttributes[`${key}_1`] = processedBaseAttributes[key];
      // eslint-disable-next-line security/detect-object-injection
      delete processedBaseAttributes[key];
    }
  }

  const { featuredImageMapping, featuredImages } = await imagePropertiesProcessing({
    visData,
    personalisationChoices,
    selections
  });
  const uvInsertImageFiles = personalisationChoices
    .filter((choice) => choice.slotType === 'UV_INSERT')
    .reduce(
      (acc, choice) => {
        if ('uvInsertImageByKey' in choice && choice.uvInsertImageByKey) {
          return { ...acc, ...choice.uvInsertImageByKey };
        }
        return acc;
      },
      {} as Record<string, File>
    );

  let items = [
    {
      id: Number(baseProductId),
      quantity: Number(baseProduct.quantity),
      properties: {
        ...featuredImages,
        ...uvInsertImageFiles,
        _check_id: checkId,
        _personalisation_choices: personalisationChoices
          // Remap into the format expected by the cart
          .map(
            ({
              choice,
              slotId,
              stepId,
              index,
              lineItemPropertyKey,
              slotType,
              font,
              visualisationGroupId,
              supportingImageLineItemPropertyKey,
              uvInsertProperties,
              uvInsertImageByKey
            }) => {
              const requiredProps = {
                c: choice,
                sl: slotId,
                st: stepId,
                i: index
              };

              if (['COMPONENT', 'SEQUENTIAL'].includes(slotType)) {
                requiredProps.c = choice;
              }

              if (slotType === 'ALPHANUMERIC') {
                requiredProps.c = lineItemPropertyKey || '';
                requiredProps.v = choice;
                requiredProps.f = font || null;
                // Store visualisationGroupId for matching in addImageUrls
                if (visualisationGroupId) {
                  requiredProps.g = visualisationGroupId;
                }
              }

              if (slotType === 'IMAGE_UPLOAD') {
                requiredProps.c = lineItemPropertyKey || '';
                requiredProps.v = '';
                // Store supportingImageLineItemPropertyKey for processing original images in addImageUrls
                if (supportingImageLineItemPropertyKey) {
                  requiredProps.s = supportingImageLineItemPropertyKey;
                }
              }

              if (slotType === 'UV_INSERT') {
                // c is already "configHandle:variantId" from buildPersonalisationChoices.
                // Attach the text LIP keys, _template_id, and vendor routing in a nested properties object.
                const uvInsertImagePlaceholders = Object.keys(uvInsertImageByKey || {}).reduce(
                  (acc, key) => ({
                    ...acc,
                    [key]: ''
                  }),
                  {} as Record<string, string>
                );
                const uvInsertChoiceProperties = {
                  ...(uvInsertProperties || {}),
                  ...uvInsertImagePlaceholders,
                  ...(vendor && { _vendorRoutingMatch: vendor })
                };
                if (Object.keys(uvInsertChoiceProperties).length > 0) {
                  requiredProps.properties = uvInsertChoiceProperties;
                }
              }

              return requiredProps;
            }
          ),
        _personalisation_base_attributes: {
          ...processedBaseAttributes,
          ...(vendor && { _vendorRoutingMatch: vendor }),
          // Add variant-level templateId, appending _2, _3 etc. if VAS config already set _template_id.
          ...(templateId &&
            (() => {
              const base = '_template_id';
              if (!(base in processedBaseAttributes)) return { [base]: templateId };
              let i = 2;
              while (`${base}_${i}` in processedBaseAttributes) i++;
              return { [`${base}_${i}`]: templateId };
            })())
        }
      }
    }
  ];

  const validationErrors = items
    .map(({ id, quantity, properties }, index) => validateCartItem({ id, quantity, properties }, index, slots))
    .filter((item) => item !== null);
  if (validationErrors.length > 0) {
    logError({
      label: 'addToCart',
      error: new Error('Validation errors occurred'),
      message: 'Cart validation failed',
      extraData: { validationErrors }
    });
    throw new Error(`Validation errors:\n${validationErrors.join('\n')}`);
  }

  // Stringify all the values for the keys under properties.
  // But skip File objects and plain string IDs - they will be handled separately.
  const stringifiedItems = items.map((item) => {
    return {
      ...item,
      properties: Object.fromEntries(
        Object.entries(item.properties).map(([key, value]) => {
          // Skip stringifying File objects - they will be handled in FormData conversion
          if (value instanceof File) {
            return [key, value];
          }
          // Skip stringifying _check_id - it's already a plain string UUID
          if (['_check_id', '_template_id'].includes(key)) {
            return [key, value];
          }
          return [key, JSON.stringify(value)];
        })
      )
    };
  });

  const formData = await convertCartPayloadToFormData({
    items: stringifiedItems,
    personalisationChoices,
    sections: ['cart-drawer'],
    visData,
    slots
  });

  return { formData, stringifiedItems, featuredImageMapping, checkId, featuredImages };
};
